import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, xcss, Text, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import AvatarGroup from '@atlaskit/avatar-group';
import Button from '@atlaskit/button/new';

import type {
	CommentType,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { CommentType as CommentTypeEnum } from '@confluence/comments-data/entry-points/useCommentsData';
import { fg } from '@confluence/feature-gating';
import { WithCurvedBranchingStyle } from '@confluence/inline-comments-common/entry-points/components';
import { useCommentsPanel } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';

import {
	type ProfilePictureInfo,
	ViewReplyOptions,
	getNumOfHiddenReplies,
	getProfilePicturesOfHiddenReplies,
	getDefaultHiddenReplies,
} from '../helpers/repliesHelpers';

const repliesTogglerStyles = {
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	height: '24px',
	left: 0,
};

const repliesTogglerBranchingStyle = css({
	position: 'relative',
	'&::before': {
		content: "''",
		position: 'absolute',
		left: 0,
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		top: 0,
		height: '60px',
	},
});

const replyBranchUnsetHeight = css({
	'&::before': {
		height: 'unset',
	},
});

const repliesTogglerBranchingStyleWithoutReply = css({
	'&::before': {
		height: '20px',
	},
});

const replyWrapperWithSpacingStyles = {
	paddingTop: token('space.100'),
	paddingBottom: token('space.100'),
	left: 0,
};

const unreadIndicatorStyle = xcss({
	alignItems: 'center',
	gap: 'space.075',
});

const pressableStyles = xcss({
	color: 'color.text.subtle',
	backgroundColor: 'color.background.neutral.subtle',
	paddingTop: 'space.050',
	paddingBottom: 'space.050',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	borderRadius: 'radius.small',
	userSelect: 'none',
	fontWeight: token('font.weight.medium'),

	':hover': {
		cursor: 'pointer',
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});
// This snippet is being used in CommentBody.tsx
const dotStyles = css({
	height: token('space.100'),
	width: token('space.100'),
	backgroundColor: token('color.background.brand.bold'),
	borderRadius: token('radius.full'),
	margin: token('space.050'),
});

const i18n = defineMessages({
	commentsPanelHideRepliesText: {
		id: 'comments-common.comment-thread.hide.replies',
		defaultMessage: 'Hide replies',
		description: 'Text for hiding replies',
	},
	commentsPanelHideReplyText: {
		id: 'comments-common.comment-thread.hide.reply',
		defaultMessage: 'Hide reply',
		description: 'Text for hiding a reply',
	},
	commentsPanelShowMoreRepliesText: {
		id: 'comments-common.comment-thread.show.more.replies',
		defaultMessage: `Show {numOfHiddenReplies, plural, one {# more reply} other {# more replies}}`,
		description: 'Text for showing more replies',
	},
	commentsPanelShowMoreRepliesTextForStandalone: {
		id: 'comments-common.comment-thread.show.more.replies.for.standalone',
		defaultMessage: `{numOfHiddenReplies, plural, one {# more reply} other {# more replies}}`,
		description: 'Text for showing more replies for standalone comment',
	},
	commentsPanelShowReplyText: {
		id: 'comments-common.comment-thread.show.reply',
		defaultMessage: 'Show reply',
		description: 'Text for showing a single hidden reply',
	},
});

const withLeftMargin = css({
	marginLeft: token('space.150'),
});

const MAX_PROFILE_PICTURES_TO_SHOW = 3;

export const RepliesToggler = ({
	replies,
	setVisibleReplies,
	unreadReplyIds,
	setReplyView,
	threadKey,
	currentReplyView,
	numOfHiddenReplies,
	commentType,
	parentCommentId,
	isParentOpen,
	canAddComments,
	shouldShowLatestReplies,
	setShouldShowLatestReplies,
	latestReplyIds,
	isStandaloneComment = false,
	onMouseEnter,
	onMouseLeave,
}: {
	replies: ReplyData[];
	setVisibleReplies: React.Dispatch<React.SetStateAction<ReplyData[]>>;
	unreadReplyIds: string[];
	setReplyView: (option: ViewReplyOptions) => void;
	threadKey: string;
	currentReplyView: ViewReplyOptions;
	numOfHiddenReplies: React.MutableRefObject<number>;
	commentType: CommentType;
	parentCommentId: string;
	isParentOpen: boolean;
	canAddComments: boolean;
	shouldShowLatestReplies: boolean;
	setShouldShowLatestReplies: (shouldShowLatestReplies: boolean) => void;
	latestReplyIds: Set<string>;
	isStandaloneComment?: boolean;
	onMouseEnter: (id?: string) => void;
	onMouseLeave: () => void;
}) => {
	const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
	const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

	const focusedCommentId = useFocusedCommentIdQueryParam();
	const replyToComment = useReplyToCommentQueryParam();

	const { removeCommentQueryParams } = useInlineCommentQueryParams();

	const { formatMessage } = useIntl();
	const [{ commentsMarkedAsRead }, { setCommentsInThreadToBeMarkedAsRead }] = useCommentsPanel();

	useEffect(() => {
		// used to expand all replies of standalone comment
		const targetCommentId = focusedCommentId || replyToComment;
		if (targetCommentId) {
			// to check if the queryParam is parentCommentId or not
			const isReplyToCommentInChain = replies?.map((reply) => reply?.id)?.includes(targetCommentId);

			if (isReplyToCommentInChain) {
				setReplyView(ViewReplyOptions.ALL);
			}
		}
	}, [focusedCommentId, replyToComment, setReplyView, replies]);

	// Memoize hidden replies calculation since it's used in multiple places
	const hiddenReplies = useMemo(() => {
		// If all replies are hidden due to current view, return all replies
		if (currentReplyView === ViewReplyOptions.HIDDEN) {
			return replies;
		}
		// Otherwise, use the default hidden replies logic
		return getDefaultHiddenReplies({
			replies,
			shouldShowLatestReplies,
			isParentOpen,
			latestReplyIds,
		});
	}, [replies, shouldShowLatestReplies, isParentOpen, latestReplyIds, currentReplyView]);

	// for the unread indicator, we only want to render it if there are any hidden unread
	const hasHiddenUnreadReplies = useMemo(() => {
		if (fg('confluence_frontend_unread_comments_fixes')) {
			const hiddenReplyIds = new Set(hiddenReplies.map((reply) => reply.id));
			return unreadReplyIds.some((replyId) => hiddenReplyIds.has(replyId));
		} else {
			const unreadRepliesNotMarkedAsRead = unreadReplyIds.filter((replyId) => {
				const markedReplies = commentsMarkedAsRead[commentType][threadKey];
				// Check if the reply id is not in the markedReplies set
				return !markedReplies || !markedReplies.has(replyId);
			});
			return (
				unreadRepliesNotMarkedAsRead.filter((replyId) => !latestReplyIds.has(replyId)).length > 0
			);
		}
	}, [unreadReplyIds, latestReplyIds, commentsMarkedAsRead, threadKey, commentType, hiddenReplies]);

	const currentNumOfHiddenReplies = fg('confluence_frontend_cp_visible_replies_refactor')
		? getNumOfHiddenReplies({ replies, latestReplyIds, currentReplyView })
		: numOfHiddenReplies.current;

	const shouldShowHideRepliesToggle = currentNumOfHiddenReplies > 0;
	const shouldShowProfilePictures =
		currentReplyView !== ViewReplyOptions.ALL && shouldShowHideRepliesToggle;
	const shouldShowShortenedBranchStyles = !isParentOpen && shouldShowHideRepliesToggle;

	const profilePicturesHiddenReplies = useRef<ProfilePictureInfo[]>(
		shouldShowProfilePictures
			? getProfilePicturesOfHiddenReplies(
					fg('confluence_frontend_unread_comments_fixes')
						? hiddenReplies
						: getDefaultHiddenReplies({
								replies,
								shouldShowLatestReplies,
								isParentOpen,
								latestReplyIds,
							}),
				)
			: [],
	);

	const showHiddenReplies = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			setReplyView(ViewReplyOptions.ALL);
			(focusedCommentId || replyToComment) && removeCommentQueryParams();

			if (!fg('confluence_frontend_cp_visible_replies_refactor')) {
				if (commentType === CommentTypeEnum.GENERAL && fg('confluence_frontend_content_wrapper')) {
					const topLevelReplyIds = new Set(
						replies.filter((reply) => reply.parentId === parentCommentId).map((reply) => reply.id),
					);
					const visibleReplies = replies.filter((reply) => topLevelReplyIds.has(reply.id));
					setVisibleReplies(visibleReplies);
				} else {
					setVisibleReplies(replies);
				}

				numOfHiddenReplies.current = 0;
			}

			profilePicturesHiddenReplies.current = [];
		},
		[
			setReplyView,
			commentType,
			numOfHiddenReplies,
			replies,
			setVisibleReplies,
			parentCommentId,
			focusedCommentId,
			replyToComment,
			removeCommentQueryParams,
		],
	);

	const hideReplies = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			// remove the queryParams if it exists
			(focusedCommentId || replyToComment) && removeCommentQueryParams();

			if (fg('confluence_frontend_cp_visible_replies_refactor')) {
				setReplyView(ViewReplyOptions.HIDDEN);
			} else {
				setReplyView(ViewReplyOptions.DEFAULT);

				setVisibleReplies([]);
				setShouldShowLatestReplies(false);

				numOfHiddenReplies.current = replies.length;
			}

			if (!fg('confluence_frontend_unread_comments_fixes')) {
				// all unread replies will be marked as read
				setCommentsInThreadToBeMarkedAsRead({
					threadKey,
					commentIds: unreadReplyIds,
					commentType,
				});
			}

			// all replies will be considered
			profilePicturesHiddenReplies.current = getProfilePicturesOfHiddenReplies(replies);
		},
		[
			threadKey,
			replies,
			setReplyView,
			setVisibleReplies,
			setCommentsInThreadToBeMarkedAsRead,
			unreadReplyIds,
			numOfHiddenReplies,
			commentType,
			setShouldShowLatestReplies,
			focusedCommentId,
			replyToComment,
			removeCommentQueryParams,
		],
	);

	return (
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions
		<div
			css={
				replies.length > 0 &&
				fg('confluence_frontend_comments_panel_v2') && [
					(canAddComments || currentNumOfHiddenReplies === 0) && repliesTogglerBranchingStyle,
					shouldShowShortenedBranchStyles && repliesTogglerBranchingStyleWithoutReply,
					currentReplyView === ViewReplyOptions.HIDDEN &&
						isStandaloneComment &&
						fg('confluence_frontend_content_wrapper') &&
						replyBranchUnsetHeight,
				]
			}
			// handles making sure we keep correct highlight state when mousing over this
			// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
			onMouseEnter={() =>
				fg('confluence_frontend_content_wrapper') && onMouseEnter(parentCommentId)
			}
			// eslint-disable-next-line @atlassian/a11y/mouse-events-have-key-events
			onMouseLeave={() => fg('confluence_frontend_content_wrapper') && onMouseLeave()}
		>
			<WithCurvedBranchingStyle
				testId="comments-panel-replies-toggler-container"
				showDefaultStyles={replies.length > 0 && fg('confluence_frontend_comments_panel_v2')}
				disableLeftProp
				customStyles={{
					...repliesTogglerStyles,
					...(fg('confluence_frontend_comments_panel_v2') ? replyWrapperWithSpacingStyles : {}),
				}}
			>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
				<div css={withLeftMargin}>
					{shouldShowHideRepliesToggle ? (
						<Pressable
							onClick={showHiddenReplies}
							testId="comments-panel-thread-show-hidden-replies"
							analyticsContext={{
								attributes: {
									name: 'showHiddenReplies',
									source: isStandaloneComment
										? AnalyticsSource.STANDALONE
										: AnalyticsSource.COMMENTS_PANEL,
									commentType,
									hasHiddenUnreadReplies,
									numOfHiddenReplies: currentNumOfHiddenReplies,
									numOfReplies: replies.length,
								},
							}}
							xcss={pressableStyles}
							interactionName="comments-panel-thread-show-hidden-replies"
						>
							{/* Standalone needs extra gap to account for margin differences in underlying avatar group*/}
							<Flex alignItems="center" gap={isStandaloneComment ? 'space.150' : 'space.050'}>
								{shouldShowProfilePictures && profilePicturesHiddenReplies.current.length > 0 && (
									<AvatarGroup
										data={profilePicturesHiddenReplies.current.map(
											(profilePicturesInfo: ProfilePictureInfo) => ({
												name: profilePicturesInfo.displayName,
												src: profilePicturesInfo.profilePicturePath,
											}),
										)}
										maxCount={MAX_PROFILE_PICTURES_TO_SHOW}
										appearance="stack"
										size="small"
										shouldPopupRenderToParent
										testId="replies-toggler-profile-pictures"
									/>
								)}
								{!fg('confluence_frontend_comments_panel_v2') && (
									<ChevronDownIcon
										spacing="compact"
										label="show-more-replies-button"
										size="small"
									/>
								)}
								<Flex xcss={unreadIndicatorStyle}>
									{replies.length === 1
										? formatMessage(i18n.commentsPanelShowReplyText)
										: isStandaloneComment
											? formatMessage(i18n.commentsPanelShowMoreRepliesTextForStandalone, {
													numOfHiddenReplies: currentNumOfHiddenReplies,
												})
											: formatMessage(i18n.commentsPanelShowMoreRepliesText, {
													numOfHiddenReplies: currentNumOfHiddenReplies,
												})}
									{hasHiddenUnreadReplies && (
										<span
											css={dotStyles}
											data-testid="comments-panel-unread-comment-replies-indicator"
										/>
									)}
								</Flex>
							</Flex>
						</Pressable>
					) : (
						<Button
							onClick={hideReplies}
							testId="comments-panel-thread-hide-replies"
							appearance="subtle"
							analyticsContext={{
								attributes: {
									name: 'hideReplies',
									source: isStandaloneComment
										? AnalyticsSource.STANDALONE
										: AnalyticsSource.COMMENTS_PANEL,
									commentType,
									numOfReplies: replies.length,
								},
							}}
							interactionName="comments-panel-thread-hide-replies"
						>
							<Flex alignItems="center" gap="space.050">
								{!fg('confluence_frontend_comments_panel_v2') && (
									<ChevronUpIcon spacing="compact" label="hide-replies-button" size="small" />
								)}
								<Text color="color.text" size="medium" weight="medium">
									{replies.length === 1
										? formatMessage(i18n.commentsPanelHideReplyText)
										: formatMessage(i18n.commentsPanelHideRepliesText)}
								</Text>
							</Flex>
						</Button>
					)}
				</div>
			</WithCurvedBranchingStyle>
		</div>
	);
};
